/**
 * Per-segment `ColumnDef` lists for the customer tables. Columns mirror the
 * legacy `customer-*-list-config` main (parent-row) columns 1-1; expandable child
 * rows, dynamic per-week note columns and bulk assign-CS are Phase 2. Built on
 * the shared cell renderers + `src/lib/format` date/number formatters.
 */
import type { ColumnDef } from '@vnetwork/ui/data';
import { formatDate, formatDateTime, formatMoney } from '@/lib/format';
import type { CustomerListRow } from '@/api/customers';
import {
  clampText,
  followerNames,
  idCell,
  industryCell,
  joinList,
  journeyFollowersCell,
  journeyInterestsCell,
  nameCell,
  statusCell,
  struckEmail,
} from './customer-cell-renderers';
import { journeyWeekColumns } from './journey-week-columns';

export type Col = ColumnDef<CustomerListRow, unknown>;

/** `MMM DD, HH:mm` — the compact head of the dead-lead "Time & Reason" cell. */
function shortStamp(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${mon} ${dd}, ${hh}:${mi}`;
}

/* ── Reusable columns ─────────────────────────────────────────────────────── */

const nameColumn: Col = {
  id: 'name',
  header: 'Name',
  cell: ({ row }) => nameCell(row.original),
};

const saleFollowersColumn: Col = {
  id: 'sale_followers',
  header: 'Sales Followers',
  cell: ({ row }) =>
    followerNames(
      row.original.customer_sale_followers,
      row.original.customer_sale_follower_names,
      'Unassigned',
    ),
};

const csFollowersColumn: Col = {
  id: 'cs_followers',
  header: 'CS Followers',
  cell: ({ row }) =>
    followerNames(
      row.original.customer_cs_followers,
      row.original.customer_cs_follower_names,
      'Unassigned',
    ),
};

/** Journey-segment Sales Followers — reads the nested `customer_journey[]`. */
const journeySaleFollowersColumn: Col = {
  id: 'sale_followers',
  header: 'Sales Followers',
  cell: ({ row }) => journeyFollowersCell(row.original),
};

/** Journey-segment Service Interests — reads the nested `customer_journey[]`. */
const journeyServiceInterestsColumn: Col = {
  id: 'service_interests',
  header: 'Service Interests',
  cell: ({ row }) => journeyInterestsCell(row.original),
};

const industryColumn: Col = {
  id: 'industry',
  header: 'Industry',
  cell: ({ row }) => industryCell(row.original.customer_industry),
};

const serviceInterestsColumn: Col = {
  id: 'service_interests',
  header: 'Service Interests',
  cell: ({ row }) => joinList(row.original.customer_service_interestings, 'None'),
};

const emailColumn: Col = {
  id: 'email',
  header: 'Email',
  cell: ({ row }) => row.original.customer_email || '—',
};

const updatedColumn: Col = {
  id: 'updated_at',
  header: 'Updated',
  cell: ({ row }) => formatDateTime(row.original.customer_updated_at),
};

/** Right-aligned "Created" (journey segments) — muted italic timestamp. */
const rightAlignedStamp = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

const journeyCreatedColumn: Col = {
  id: 'created_at',
  header: 'Created',
  cell: ({ row }) => (
    <span style={{ display: 'block', textAlign: 'right', color: 'var(--vn-fg-muted)', fontStyle: 'italic', ...rightAlignedStamp }}>
      {formatDateTime(row.original.customer_created_at)}
    </span>
  ),
};

const journeyUpdatedColumn: Col = {
  id: 'updated_at',
  header: 'Last Updated',
  cell: ({ row }) => (
    <span style={{ display: 'block', textAlign: 'right', color: 'var(--vn-fg-muted)', fontStyle: 'italic', ...rightAlignedStamp }}>
      {formatDateTime(row.original.customer_updated_at)}
    </span>
  ),
};

/* ── Per-segment column lists ─────────────────────────────────────────────── */

/**
 * Qualified Prospects & Cool Lead share the journey column set. Followers +
 * service interests come from the nested `customer_journey[]` (journey-specific
 * accessors), NOT the flat keys the service segments use.
 */
export const journeyColumns: Col[] = [
  nameColumn,
  journeySaleFollowersColumn,
  journeyServiceInterestsColumn,
  industryColumn,
  emailColumn,
  journeyCreatedColumn,
  journeyUpdatedColumn,
  // Dynamic last-4-ISO-weeks activity columns (W1 oldest → W4 current).
  ...journeyWeekColumns(),
];

export const activeColumns: Col[] = [
  nameColumn,
  saleFollowersColumn,
  csFollowersColumn,
  industryColumn,
  {
    id: 'total_payment',
    header: () => <span style={{ display: 'block', textAlign: 'right' }}>Total Payment</span>,
    cell: ({ row }) => (
      <span style={{ display: 'block', textAlign: 'right' }}>{formatMoney(row.original.total_payment)}</span>
    ),
  },
  {
    id: 'first_service_start_date',
    header: 'First Service Start',
    cell: ({ row }) => formatDate(row.original.first_service_start_date),
  },
  updatedColumn,
];

export const trialColumns: Col[] = [nameColumn, saleFollowersColumn, csFollowersColumn, industryColumn];

/** Stop (cancelled) = trial columns + last service off date. */
export const cancelledColumns: Col[] = [
  ...trialColumns,
  {
    id: 'last_service_off_date',
    header: 'Last Service Off',
    cell: ({ row }) => formatDate(row.original.last_service_off_date),
  },
];

export const deadLeadColumns: Col[] = [
  nameColumn,
  saleFollowersColumn,
  industryColumn,
  serviceInterestsColumn,
  {
    id: 'time_and_reason',
    header: 'Time & Reason',
    cell: ({ row }) => {
      const stamp = shortStamp(row.original.customer_updated_at);
      const note = row.original.customer_note || '—';
      return (
        <span title={`${stamp}: ${note}`} style={{ display: 'flex', gap: 'var(--vn-space-2)', minWidth: 0 }}>
          <span style={{ whiteSpace: 'nowrap' }}>{stamp}</span>
          <span style={{ color: 'var(--vn-fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {note}
          </span>
        </span>
      );
    },
  },
];

export const deletedColumns: Col[] = [
  {
    id: 'id',
    header: 'ID',
    cell: ({ row }) => idCell(row.original),
  },
  {
    id: 'email',
    header: 'Email',
    cell: ({ row }) => struckEmail(row.original.customer_email),
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => statusCell(row.original.customer_status),
  },
  {
    id: 'deletion_reason',
    header: 'Deletion Reason',
    cell: ({ row }) => clampText(row.original.deletion_reason),
  },
  {
    id: 'deleted_by',
    header: 'Deleted By',
    cell: ({ row }) => row.original.deleted_by_name || '—',
  },
  {
    id: 'deleted_at',
    header: 'Deleted At',
    cell: ({ row }) => formatDateTime(row.original.deleted_at),
  },
];
